/**
 * xlsxShim.js
 * Drop-in compatibility shim that replaces the `xlsx` package with ExcelJS.
 *
 * API surface mirrored:
 *   XLSX.utils.book_new / aoa_to_sheet / book_append_sheet / encode_cell / sheet_to_json
 *   XLSX.writeFile(wb, filename)  - async, returns Promise<void>
 *   XLSX.read(buffer, opts)       - async, returns Promise<shimWorkbook>
 *   XLSX.SSF.parse_date_code(n)   - sync, returns {y,m,d,H,M,S}
 *
 * All callers of writeFile / read must await the result.
 * All other utils (book_new, aoa_to_sheet, book_append_sheet, encode_cell) remain sync.
 */
import ExcelJS from 'exceljs';

// ── Column-letter encoder (0-based → "A", "B", …, "Z", "AA", …) ──────────────
function colLetter(c) {
    let s = '';
    let n = c + 1;
    while (n > 0) {
        const rem = (n - 1) % 26;
        s = String.fromCharCode(65 + rem) + s;
        n = Math.floor((n - 1) / 26);
    }
    return s;
}

// ── Trigger browser file download from an ArrayBuffer ────────────────────────
function triggerDownload(buffer, filename) {
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── Flush a buffered shim-worksheet into a real ExcelJS worksheet ─────────────
function flushSheet(wb, shimWs, name) {
    const ws = wb.addWorksheet(name);

    // Rows
    (shimWs._data || []).forEach(row => ws.addRow(row == null ? [] : row));

    // Column widths: shimWs['!cols'] = [{wch: n}, …]
    (shimWs['!cols'] || []).forEach((col, i) => {
        if (col?.wch) ws.getColumn(i + 1).width = col.wch;
    });

    // Row heights: shimWs['!rows'] = [{hpt: n}, …]
    (shimWs['!rows'] || []).forEach((row, i) => {
        if (row?.hpt) ws.getRow(i + 1).height = row.hpt * 0.75; // hpt→pt, ExcelJS uses pt
    });

    // Merges: shimWs['!merges'] = [{s:{r,c}, e:{r,c}}, …]  (0-indexed)
    (shimWs['!merges'] || []).forEach(({ s, e }) => {
        try { ws.mergeCells(s.r + 1, s.c + 1, e.r + 1, e.c + 1); } catch { /* skip */ }
    });

    // Per-cell number formats: shimWs["A1"] = {z: '#,##0'}
    Object.entries(shimWs).forEach(([key, val]) => {
        if (key.startsWith('!') || key === '_data') return;
        if (val?.z) {
            try { ws.getCell(key).numFmt = val.z; } catch { /* skip */ }
        }
    });

    return ws;
}

// ═════════════════════════════════════════════════════════════════════════════
// Public XLSX shim
// ═════════════════════════════════════════════════════════════════════════════
export const XLSX = {
    utils: {
        /** Creates a new shim workbook (plain object - not an ExcelJS.Workbook). */
        book_new: () => ({ _sheets: [] }),

        /** Wraps array-of-arrays data in a shim worksheet object. */
        aoa_to_sheet: (data) => ({ _data: data }),

        /** Queues a shim worksheet on the workbook for later flushing. */
        book_append_sheet: (wb, ws, name) => {
            wb._sheets.push({ ws, name: name || `Sheet${wb._sheets.length + 1}` });
        },

        /** Mirrors XLSX.utils.encode_cell({r, c}) - 0-based → "A1" address. */
        encode_cell: ({ r, c }) => `${colLetter(c)}${r + 1}`,

        /**
         * Converts an ExcelJS worksheet to an array-of-arrays.
         * Mirrors XLSX.utils.sheet_to_json(ws, {header: 1}).
         */
        sheet_to_json: (ws, opts = {}) => {
            const rows = [];
            if (!ws || typeof ws.eachRow !== 'function') return rows;
            ws.eachRow((row) => {
                // ExcelJS row.values is 1-indexed; index 0 is undefined
                const values = Array.isArray(row.values) ? row.values.slice(1) : [];
                const cells = values.map(v => {
                    if (v == null) return opts.defval !== undefined ? opts.defval : null;
                    if (v && typeof v === 'object' && v.richText) {
                        // Rich-text cell → flatten to string
                        return v.richText.map(t => t.text || '').join('');
                    }
                    if (v && typeof v === 'object' && v.result !== undefined) {
                        // Formula cell - use computed result
                        return v.result;
                    }
                    return v;
                });
                rows.push(cells);
            });
            return rows;
        },
    },

    /**
     * Compile toutes les feuilles tamponnées en un vrai classeur ExcelJS et
     * renvoie le contenu binaire (ArrayBuffer), SANS déclencher de téléchargement.
     * Utile pour à la fois télécharger ET déposer le même fichier ailleurs.
     * @returns {Promise<ArrayBuffer>}
     */
    toBuffer: async (shimWb) => {
        const wb = new ExcelJS.Workbook();
        for (const { ws, name } of shimWb._sheets) {
            flushSheet(wb, ws, name);
        }
        return wb.xlsx.writeBuffer();
    },

    /** Déclenche un téléchargement navigateur depuis un ArrayBuffer. */
    download: (buffer, filename) => triggerDownload(buffer, filename),

    /**
     * Flushes all buffered shim-sheets to a real ExcelJS workbook and
     * triggers a browser file download.
     * @returns {Promise<void>}
     */
    writeFile: async (shimWb, filename) => {
        const buffer = await XLSX.toBuffer(shimWb);
        triggerDownload(buffer, filename);
    },

    /**
     * Reads an Excel file from an ArrayBuffer and returns a shim workbook
     * that exposes .SheetNames and .Sheets (ExcelJS worksheet objects).
     * Use XLSX.utils.sheet_to_json() to iterate rows.
     * @param {ArrayBuffer|Uint8Array} data
     * @returns {Promise<object>}
     */
    read: async (data) => {
        const wb = new ExcelJS.Workbook();
        const buf = data instanceof Uint8Array ? data.buffer : data;
        await wb.xlsx.load(buf);
        // Build xlsx-compatible .Sheets / .SheetNames accessors
        wb.SheetNames = wb.worksheets.map(ws => ws.name);
        wb.Sheets = Object.fromEntries(wb.worksheets.map(ws => [ws.name, ws]));
        return wb;
    },

    SSF: {
        /**
         * Converts an Excel date serial number to date-parts object.
         * Matches the shape returned by the original xlsx SSF.parse_date_code().
         * Excel serial 1 = 1900-01-01, with the phantom Feb-29-1900 bug accounted for.
         */
        parse_date_code: (n) => {
            if (!n || typeof n !== 'number') return { y: 0, m: 0, d: 0, H: 0, M: 0, S: 0 };
            // Shift past Excel's phantom leap day (serial 60 = Feb 29, 1900 that never existed)
            const adjusted = n > 59 ? n - 1 : n;
            const ms = (adjusted - 1) * 86400000;
            const date = new Date(Date.UTC(1900, 0, 1) + ms);
            return {
                y: date.getUTCFullYear(),
                m: date.getUTCMonth() + 1,
                d: date.getUTCDate(),
                H: date.getUTCHours(),
                M: date.getUTCMinutes(),
                S: date.getUTCSeconds(),
            };
        },
    },
};
